import { useEffect } from 'react';
import { type LiveItem, type LiveMetric, type ResourcesSummary } from '../../api';
import { useData } from '../../useData';
import { ErrorMsg } from '../../components/Modal';

const money = (v: number, cur: string) => new Intl.NumberFormat('ro-RO', { style: 'currency', currency: cur || 'EUR' }).format(v);
const time = (iso: string) => new Date(iso).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

const METRIC_LABELS: Record<string, string> = {
  CpuPercentage: 'Procesor', MemoryPercentage: 'Memorie', Requests: 'Cereri (ultimele 5 min)', Http5xx: 'Erori server (5 min)',
  cpu_percent: 'Procesor', storage_percent: 'Spațiu folosit', app_cpu_billed: 'vCore-secunde facturate (5 min)',
};

// Administrare → Resurse: ce folosește aplicația în Azure, starea în timp real și costul lunii curente.
export function ResourcesPage() {
  const summary = useData<ResourcesSummary>('/admin/resources');
  const live = useData<{ updatedAt: string; items: LiveItem[] }>('/admin/resources/live');

  // metricile live se reîmprospătează la 30 de secunde, costurile la 5 minute
  useEffect(() => {
    const a = setInterval(live.reload, 30_000);
    const b = setInterval(summary.reload, 5 * 60_000);
    return () => { clearInterval(a); clearInterval(b); };
  }, [live.reload, summary.reload]);

  const s = summary.data;
  const budgetPct = s?.budget?.amount ? Math.min(100, Math.round(((s.budget.currentSpend ?? s.costMonthToDate) / s.budget.amount) * 100)) : null;

  return (
    <>
      <div className="page-head">
        <h1>Resurse</h1>
        <button onClick={() => { summary.reload(); live.reload(); }}>Reîmprospătează</button>
      </div>
      <ErrorMsg error={summary.error} />

      {s && (
        <div className="stat-row">
          <div className="stat">
            <div className="stat-label">Cost luna curentă</div>
            <div className="stat-value">{money(s.costMonthToDate, s.currency)}</div>
            <div className="muted small">{s.costError ? 'Costurile nu sunt disponibile încă' : s.costNote}</div>
          </div>
          {s.budget && (
            <div className="stat">
              <div className="stat-label">Buget lunar</div>
              <div className="stat-value">{money(s.budget.amount, s.currency)}</div>
              <div className="meter"><div style={{ width: `${budgetPct ?? 0}%`, background: (budgetPct ?? 0) >= 80 ? 'var(--danger)' : 'var(--green)' }} /></div>
              <div className="muted small">{budgetPct ?? 0}% folosit</div>
            </div>
          )}
          <div className="stat">
            <div className="stat-label">Resurse active</div>
            <div className="stat-value">{s.resources.length}</div>
            <div className="muted small">actualizat {time(s.updatedAt)}</div>
          </div>
        </div>
      )}

      <section>
        <h2>În timp real <span className="muted small">(ultima oră · se actualizează la 30 s{live.data ? ` · ${time(live.data.updatedAt)}` : ''})</span></h2>
        <ErrorMsg error={live.error} />
        <div className="cards">
          {live.data?.items.map((it) => (
            <div key={it.id} className="card">
              <div className="muted small">{it.kind}</div>
              <h3>{it.name}</h3>
              {it.metrics.map((m) => <MetricLine key={m.name} m={m} />)}
            </div>
          ))}
          {live.loading && !live.data && <p className="muted">Se încarcă…</p>}
        </div>
      </section>

      {s && (
        <section>
          <h2>Toate resursele</h2>
          {s.costError && <p className="muted small">Detaliu cost: {s.costError.slice(0, 200)}</p>}
          <table>
            <thead><tr><th>Resursă</th><th>Tip</th><th>Mediu</th><th>Nivel</th><th>Regiune</th><th className="right">Cost luna curentă</th></tr></thead>
            <tbody>
              {s.resources.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td>{r.typeName}</td>
                  <td>{r.environment ?? <span className="muted">—</span>}</td>
                  <td className="muted small">{r.sku ?? '—'}</td>
                  <td className="muted small">{r.location}</td>
                  <td className="right">{money(r.costMonthToDate, s.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted small">Agenții AI și alte servicii noi vor apărea automat în această listă când sunt adăugate.</p>
        </section>
      )}
    </>
  );
}

function MetricLine({ m }: { m: LiveMetric }) {
  if (m.name === 'error') return <p className="error small">{m.label}</p>;
  const isPct = m.unit === 'Percent';
  const value = m.last === null ? '—' : isPct ? `${Math.round(m.last)}%` : String(Math.round(m.last * 10) / 10);
  const values = m.points.map((p) => p.v ?? 0);
  const max = Math.max(isPct ? 100 : 1, ...values);
  return (
    <div className="metric">
      <div className="metric-head"><span>{METRIC_LABELS[m.name] ?? m.label}</span><strong>{value}</strong></div>
      <svg className="spark" viewBox={`0 0 ${Math.max(values.length - 1, 1)} 20`} preserveAspectRatio="none" aria-hidden="true">
        <polyline fill="none" stroke="var(--green)" strokeWidth="1.5" vectorEffect="non-scaling-stroke"
          points={values.map((v, i) => `${i},${20 - (v / max) * 18}`).join(' ')} />
      </svg>
    </div>
  );
}
